import React, { useMemo, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import {
  BanknotesIcon,
  BuildingOfficeIcon,
  CalendarDaysIcon,
  DocumentTextIcon,
  MagnifyingGlassIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import PageLayout from '../../components/PageLayout';
import {
  AdminEmptyState,
  AdminPageHeader,
  AdminStatCard,
  AdminStatGrid,
  AdminTableCard,
} from '../../components/AdminSurface';
import { adminAPI, apiClient } from '../../services/api';
import { Invoice, InvoicePaymentActivity, AdminInvoiceListResponse } from '../../types/invoice';
import { InvoiceCustomerDetails } from '../../types/customer';
import InvoiceDrawer from '../../components/invoices/InvoiceDrawer';
import GroupedPaymentRequestDrawer from '../../components/invoices/GroupedPaymentRequestDrawer';
import CustomerDetailDrawer from '../../components/customer/CustomerDetailDrawer';
import AccountDetailDrawer from '../../components/account/AccountDetailDrawer';
import ReservationDetailDrawer from '../../components/reservations/ReservationDetailDrawer';
import { AccountSummary } from '../../components/account/AccountDetailContent';
import { useAuth } from '../../context/AuthContext';
import {
  formatCurrencyCents,
  formatDateShort,
  invoiceCustomerLabel,
} from '../../utils/invoiceFormatting';

type PaymentsDrawerState =
  | { type: 'invoice' }
  | { type: 'group'; requestId: string }
  | { type: 'customer'; customer: InvoiceCustomerDetails }
  | { type: 'account'; accountId: string }
  | { type: 'reservation'; reservationId: string }
  | null;

export default function AdminPayments() {
  const { user } = useAuth();
  const isPlatformAdmin = Boolean((user as any)?.is_platform_admin);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);
  const limit = 50;

  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [drawerState, setDrawerState] = useState<PaymentsDrawerState>(null);
  const [paymentActivity, setPaymentActivity] = useState<InvoicePaymentActivity | null>(null);
  const [paymentActivityLoading, setPaymentActivityLoading] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);

  // ── Paid invoices query ──────────────────────────────────────────────────────

  const queryParams = useMemo(() => ({
    status: 'paid' as const,
    search: search.trim() || undefined,
    startDate: startDate || undefined,
    endDate: endDate || undefined,
    page,
    limit,
  }), [search, startDate, endDate, page, limit]);

  const { data: invoicesData, isLoading, refetch } = useQuery<AdminInvoiceListResponse>(
    ['admin-payments', queryParams],
    () => adminAPI.getAllInvoices(queryParams).then((r) => r.data),
    { keepPreviousData: true },
  );

  // ── Accounts pre-fetch for name resolution ───────────────────────────────────

  const { data: accountsData } = useQuery(
    'admin-payments-accounts',
    () => adminAPI.getAccounts({ limit: 500 }).then((r) => r.data),
    { staleTime: 5 * 60 * 1000 },
  );

  const accountsById = useMemo<Map<string, string>>(
    () => new Map(((accountsData as any)?.accounts || []).map((a: AccountSummary) => [a.id, a.name])),
    [accountsData],
  );

  // ── Derived data ─────────────────────────────────────────────────────────────

  const payments = useMemo(() => {
    const invoices = invoicesData?.invoices || [];
    return [...invoices].sort((a, b) => {
      const aTime = a.paid_at ? new Date(a.paid_at).getTime() : 0;
      const bTime = b.paid_at ? new Date(b.paid_at).getTime() : 0;
      return bTime - aTime;
    });
  }, [invoicesData]);

  const pagination = invoicesData?.pagination;
  const totalPages = pagination?.totalPages || 1;
  const totalAmountPaid = invoicesData?.summary?.paid_amount ?? 0;

  // ── Utilities ────────────────────────────────────────────────────────────────

  const formatActivityTimestamp = (value: string | number | null | undefined) => {
    if (!value) return 'N/A';
    if (typeof value === 'number') return new Date(value * 1000).toLocaleString();
    return new Date(value).toLocaleString();
  };

  const activitySortValue = (value: string | number | null | undefined) => {
    if (!value) return Number.MIN_SAFE_INTEGER;
    if (typeof value === 'number') return value * 1000;
    const parsed = new Date(value).getTime();
    return Number.isFinite(parsed) ? parsed : Number.MIN_SAFE_INTEGER;
  };

  const getStripeObjectUrl = (objectId: string | null | undefined, connectedAccountId?: string | null) => {
    if (!objectId) return null;
    const hostname = window.location.hostname.toLowerCase();
    const isSandbox =
      process.env.NODE_ENV !== 'production' ||
      hostname.includes('localhost') ||
      hostname.includes('127.0.0.1') ||
      hostname.includes('dev') ||
      hostname.includes('staging') ||
      hostname.includes('sandbox');
    const base = isSandbox
      ? 'https://dashboard.stripe.com/test'
      : 'https://dashboard.stripe.com';
    const url = `${base}/payments/${objectId}`;
    return connectedAccountId ? `${url}?account=${encodeURIComponent(connectedAccountId)}` : url;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1_048_576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1_048_576).toFixed(1)} MB`;
  };

  const feeBreakdown = useMemo(() => {
    if (!selectedInvoice) return null;
    if (paymentActivity?.live_fee_breakdown) {
      const fb = paymentActivity.live_fee_breakdown;
      return {
        stripeFee: fb.stripe_fee,
        platformFee: fb.platform_fee,
        totalFees: fb.total_fees,
        netAmount: fb.net_amount,
        amountPaid: fb.amount_paid,
        source: 'live' as const,
      };
    }
    const { stripe_fee: sf, platform_fee: pf, net_amount: na } = selectedInvoice;
    if (sf == null && pf == null && na == null) return null;
    return {
      stripeFee: sf ?? null,
      platformFee: pf ?? null,
      totalFees: sf != null && pf != null ? sf + pf : null,
      netAmount: na ?? null,
      amountPaid: selectedInvoice.amount_paid,
      source: 'local' as const,
    };
  }, [selectedInvoice, paymentActivity]);

  // ── Action handlers ──────────────────────────────────────────────────────────

  const handleOpenInvoice = async (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setDrawerState({ type: 'invoice' });
    setPaymentActivity(null);
    setPaymentActivityLoading(true);
    try {
      const response = await adminAPI.getInvoicePaymentActivity(invoice.id);
      setPaymentActivity(response.data);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to load payment activity');
    } finally {
      setPaymentActivityLoading(false);
    }
  };

  const handleAttachmentDownload = async (attachmentId: string, originalName: string) => {
    if (!selectedInvoice) return;
    try {
      const response = await apiClient.get(
        `/invoices/${selectedInvoice.id}/attachments/${attachmentId}/download`,
        { responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', originalName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to download attachment');
    }
  };

  const handleAttachmentDelete = async (attachmentId: string) => {
    if (!selectedInvoice) return;
    try {
      await adminAPI.deleteInvoiceAttachment(selectedInvoice.id, attachmentId);
      toast.success('Attachment deleted');
      setSelectedInvoice({
        ...selectedInvoice,
        attachments: selectedInvoice.attachments?.filter((a) => a.id !== attachmentId),
      });
      refetch();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete attachment');
    }
  };

  const handleAttachmentUpload = async (file: File) => {
    if (!selectedInvoice) return;
    setUploadingAttachment(true);
    try {
      const { data: attachment } = await adminAPI.uploadInvoiceAttachment(selectedInvoice.id, file);
      toast.success('Attachment uploaded');
      setSelectedInvoice({
        ...selectedInvoice,
        attachments: [...(selectedInvoice.attachments || []), attachment],
      });
      refetch();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to upload attachment');
    } finally {
      setUploadingAttachment(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <PageLayout>
      <div className="space-y-6">
        <AdminPageHeader
          title="Payments"
          description="Chronological record of all received payments across invoices and grouped requests."
          eyebrow="Tenant billing"
        />

        <AdminStatGrid>
          <AdminStatCard
            label="Total Payments"
            value={pagination?.total ?? 0}
            icon={<DocumentTextIcon className="h-5 w-5" />}
            tone="primary"
          />
          <AdminStatCard
            label="Total Collected"
            value={formatCurrencyCents(totalAmountPaid)}
            icon={<BanknotesIcon className="h-5 w-5" />}
            tone="success"
          />
        </AdminStatGrid>

        {/* Filters */}
        <div className="flex flex-wrap items-end gap-3">
          <div className="relative min-w-[220px] flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-on-surface-variant" />
            <input
              type="text"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="input-field pl-9"
              placeholder="Search invoice or customer…"
            />
          </div>
          <div className="flex items-center gap-2">
            <CalendarDaysIcon className="h-4 w-4 flex-shrink-0 text-on-surface-variant" />
            <input
              type="date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
              className="input-field"
            />
            <span className="text-xs text-on-surface-variant">to</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
              className="input-field"
            />
          </div>
          {(search || startDate || endDate) && (
            <button
              type="button"
              onClick={() => { setSearch(''); setStartDate(''); setEndDate(''); setPage(1); }}
              className="btn-secondary text-sm"
            >
              Clear
            </button>
          )}
        </div>

        {/* Table */}
        <AdminTableCard
          meta={
            !isLoading && payments.length > 0 ? (
              <span>
                {payments.length} payments on this page
                {pagination?.total != null && ` · ${pagination.total} total · Page ${page} of ${totalPages}`}
              </span>
            ) : undefined
          }
        >
          <table className="admin-table-grid min-w-full">
            <thead className="admin-table-head">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-on-surface-variant">
                  Date Paid
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-on-surface-variant">
                  Invoice / Request
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-on-surface-variant">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-on-surface-variant">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-on-surface-variant">
                  Account
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-on-surface-variant">
                  Reservation
                </th>
              </tr>
            </thead>
            {isLoading ? (
              <tbody className="admin-table-body">
                {Array.from({ length: 5 }).map((_, i) => (
                  <tr key={`skeleton-${i}`} className="admin-table-row animate-pulse">
                    <td className="px-6 py-4"><div className="h-4 w-24 rounded bg-surface-container-low" /></td>
                    <td className="px-6 py-4">
                      <div className="space-y-2">
                        <div className="h-4 w-32 rounded bg-surface-container-low" />
                        <div className="h-3 w-20 rounded bg-surface-container-low" />
                      </div>
                    </td>
                    <td className="px-6 py-4"><div className="h-4 w-20 rounded bg-surface-container-low" /></td>
                    <td className="px-6 py-4">
                      <div className="space-y-1.5">
                        <div className="h-4 w-28 rounded bg-surface-container-low" />
                        <div className="h-3 w-20 rounded bg-surface-container-low" />
                      </div>
                    </td>
                    <td className="px-6 py-4"><div className="h-4 w-24 rounded bg-surface-container-low" /></td>
                    <td className="px-6 py-4"><div className="h-6 w-20 rounded bg-surface-container-low" /></td>
                  </tr>
                ))}
              </tbody>
            ) : payments.length > 0 ? (
              <tbody className="admin-table-body">
                {payments.map((invoice) => {
                  const customerName = invoice.customer_details
                    ? invoiceCustomerLabel(invoice.customer_details)
                    : null;
                  const accountName = invoice.account_id
                    ? (accountsById.get(invoice.account_id) || 'Account')
                    : null;

                  return (
                    <tr
                      key={invoice.id}
                      className="admin-table-row cursor-pointer"
                      onClick={() => handleOpenInvoice(invoice)}
                    >
                      <td className="px-6 py-4 tabular-nums">
                        <span className="text-sm text-on-surface-variant">
                          {invoice.paid_at ? formatDateShort(invoice.paid_at) : '—'}
                        </span>
                      </td>

                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-on-surface">{invoice.invoice_number}</div>
                        {invoice.grouped_payment_request_id && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDrawerState({
                                type: 'group',
                                requestId: invoice.grouped_payment_request_id!,
                              });
                            }}
                            className="admin-action-pill mt-1 inline-flex items-center gap-1 rounded-md bg-surface-container-low px-2.5 py-1 text-xs font-medium text-on-surface hover:bg-surface-container-highest"
                          >
                            <DocumentTextIcon className="h-3 w-3" />
                            Grouped request
                          </button>
                        )}
                      </td>

                      <td className="px-6 py-4 tabular-nums">
                        <div className="text-sm font-medium text-on-surface">
                          {formatCurrencyCents(invoice.amount_paid, invoice.currency)}
                        </div>
                      </td>

                      <td className="px-6 py-4">
                        {invoice.customer_details ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDrawerState({
                                type: 'customer',
                                customer: invoice.customer_details,
                              });
                            }}
                            className="text-left transition-colors hover:text-primary"
                          >
                            <div className="text-sm font-medium text-on-surface hover:underline">
                              {customerName}
                            </div>
                            <div className="text-xs text-on-surface-variant">
                              {invoice.customer_details.email || 'No email'}
                            </div>
                          </button>
                        ) : (
                          <span className="text-sm text-on-surface-variant">—</span>
                        )}
                      </td>

                      <td className="px-6 py-4">
                        {invoice.account_id ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDrawerState({
                                type: 'account',
                                accountId: invoice.account_id!,
                              });
                            }}
                            className="text-left transition-colors hover:text-primary"
                          >
                            <div className="flex items-center gap-1.5">
                              <BuildingOfficeIcon className="h-4 w-4 flex-shrink-0 text-on-surface-variant" />
                              <span className="text-sm font-medium text-on-surface hover:underline">
                                {accountName}
                              </span>
                            </div>
                          </button>
                        ) : (
                          <span className="text-sm text-on-surface-variant">—</span>
                        )}
                      </td>

                      <td className="px-6 py-4">
                        {invoice.reservation_id ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDrawerState({
                                type: 'reservation',
                                reservationId: invoice.reservation_id!,
                              });
                            }}
                            className="admin-action-pill inline-flex items-center gap-1.5 rounded-md bg-surface-container-low px-2.5 py-1 text-xs font-medium text-on-surface hover:bg-surface-container-highest"
                          >
                            <CalendarDaysIcon className="h-3.5 w-3.5 flex-shrink-0" />
                            View
                          </button>
                        ) : (
                          <span className="text-sm text-on-surface-variant">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            ) : null}
          </table>

          {!isLoading && payments.length === 0 && (
            <AdminEmptyState
              icon={<BanknotesIcon className="h-6 w-6" />}
              title="No payments found"
              description={
                search || startDate || endDate
                  ? 'Try adjusting your filters'
                  : 'Payments will appear here once invoices are paid'
              }
            />
          )}

          {totalPages > 1 && (
            <div className="admin-table-toolbar flex items-center justify-between px-6 py-3">
              <p className="text-xs text-on-surface-variant">
                Page {page} of {totalPages} · {pagination?.total} payments
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="admin-action-pill rounded-md bg-surface-container-low px-3 py-1.5 text-xs font-medium text-on-surface hover:bg-surface-container-highest disabled:opacity-40"
                >
                  Previous
                </button>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="admin-action-pill rounded-md bg-surface-container-low px-3 py-1.5 text-xs font-medium text-on-surface hover:bg-surface-container-highest disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </AdminTableCard>

        {/* Hidden file input for attachment upload */}
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleAttachmentUpload(file);
          }}
        />
      </div>

      {/* ── Drawers ─────────────────────────────────────────────────────────── */}

      {drawerState?.type === 'invoice' && selectedInvoice && (
        <InvoiceDrawer
          open={true}
          selectedInvoice={selectedInvoice}
          paymentActivityLoading={paymentActivityLoading}
          paymentActivity={paymentActivity}
          feeBreakdown={feeBreakdown}
          formatActivityTimestamp={formatActivityTimestamp}
          activitySortValue={activitySortValue}
          getStripeObjectUrl={getStripeObjectUrl}
          onClose={() => { setDrawerState(null); setSelectedInvoice(null); }}
          onAction={() => {}}
          actions={[]}
          onUploadFileClick={() => fileInputRef.current?.click()}
          uploadingAttachment={uploadingAttachment}
          onAttachmentDownload={handleAttachmentDownload}
          onAttachmentDelete={handleAttachmentDelete}
          formatFileSize={formatFileSize}
          isPlatformAdmin={isPlatformAdmin}
          onSaveEdit={async (payload) => {
            await apiClient.put(`/invoices/admin/${selectedInvoice.id}`, payload);
            toast.success('Invoice updated');
            refetch();
          }}
        />
      )}

      {drawerState?.type === 'group' && (
        <GroupedPaymentRequestDrawer
          open={true}
          requestId={drawerState.requestId}
          onClose={() => setDrawerState(null)}
          onRequestUpdated={() => { void refetch(); }}
        />
      )}

      {drawerState?.type === 'customer' && (
        <CustomerDetailDrawer
          open={true}
          customer={{
            id: drawerState.customer.email || invoiceCustomerLabel(drawerState.customer),
            customerName: invoiceCustomerLabel(drawerState.customer),
            customerEmail: drawerState.customer.email,
            email: drawerState.customer.email,
            phone: drawerState.customer.phone || drawerState.customer.company_contact_phone,
            customer_status: drawerState.customer.customer_status,
            customer_type: drawerState.customer.customer_type,
            company_name: drawerState.customer.company_name,
          }}
          subtitlePrefix="Customer"
          onClose={() => setDrawerState(null)}
        />
      )}

      {drawerState?.type === 'account' && (
        <AccountDetailDrawer
          open={true}
          account={{ id: drawerState.accountId, name: accountsById.get(drawerState.accountId) || 'Account' }}
          onClose={() => setDrawerState(null)}
          onAccountUpdated={() => {}}
        />
      )}

      {drawerState?.type === 'reservation' && (
        <ReservationDetailDrawer
          open={true}
          reservationId={drawerState.reservationId}
          onClose={() => setDrawerState(null)}
        />
      )}
    </PageLayout>
  );
}
